import type { PenAnchorPoint } from '@/lib/elements/types/pen-anchor-point'
import type { PathCommand } from '@/lib/elements/types/path-command'

/** Build path string + PathCommand[] in local coords (0,0 = bounding box top-left). */
export function buildPathFromPoints(
  points: PenAnchorPoint[],
  isClosed: boolean,
  minX: number,
  minY: number
): { d: string; pathCommands: PathCommand[] } {
  const p0 = points[0]
  let d = `M ${p0.x - minX} ${p0.y - minY}`
  const pathCommands: PathCommand[] = [{ moveTo: { x: p0.x - minX, y: p0.y - minY } }]

  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1]
    const curr = points[i]
    const cp1x = prev.handleOut?.x != null ? prev.handleOut.x - minX : prev.x - minX
    const cp1y = prev.handleOut?.y != null ? prev.handleOut.y - minY : prev.y - minY
    const cp2x = curr.handleIn?.x != null ? curr.handleIn.x - minX : curr.x - minX
    const cp2y = curr.handleIn?.y != null ? curr.handleIn.y - minY : curr.y - minY
    const ex = curr.x - minX
    const ey = curr.y - minY

    if (prev.handleOut || curr.handleIn) {
      d += ` C ${cp1x} ${cp1y} ${cp2x} ${cp2y} ${ex} ${ey}`
      pathCommands.push({
        curveTo: {
          controlX: cp1x,
          controlY: cp1y,
          controlX2: cp2x,
          controlY2: cp2y,
          x: ex,
          y: ey,
        },
      })
    } else {
      d += ` L ${ex} ${ey}`
      pathCommands.push({ lineTo: { x: ex, y: ey } })
    }
  }

  if (isClosed && points.length >= 2) {
    const last = points[points.length - 1]
    const cp1x = last.handleOut?.x != null ? last.handleOut.x - minX : last.x - minX
    const cp1y = last.handleOut?.y != null ? last.handleOut.y - minY : last.y - minY
    const cp2x = p0.handleIn?.x != null ? p0.handleIn.x - minX : p0.x - minX
    const cp2y = p0.handleIn?.y != null ? p0.handleIn.y - minY : p0.y - minY

    if (last.handleOut || p0.handleIn) {
      d += ` C ${cp1x} ${cp1y} ${cp2x} ${cp2y} ${p0.x - minX} ${p0.y - minY}`
      pathCommands.push({
        curveTo: {
          controlX: cp1x,
          controlY: cp1y,
          controlX2: cp2x,
          controlY2: cp2y,
          x: p0.x - minX,
          y: p0.y - minY,
        },
      })
    } else {
      // Straight-line close: add explicit lineTo so the closing segment survives
      // the pathCommands round-trip. Z is not a PathCommand, so without this
      // the closing edge disappears after reload.
      pathCommands.push({ lineTo: { x: p0.x - minX, y: p0.y - minY } })
    }

    d += ' Z'
  }

  return { d, pathCommands }
}
